import React, { useState, useEffect } from 'react';
import { api } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';

/**
 * WPSL-style page hero banner.
 * - Full-bleed image (no rounded card), edge-to-edge
 * - Diagonal red→blue gradient overlay (matches wpslsoccer.com)
 * - Centered Montserrat-thin title (uppercase tracking)
 * - Optional subtitle in Lato
 */
export const PageHeroBanner = ({
  pageSlug,
  fallbackTitle,
  fallbackSubtitle,
  height = 360,
}) => {
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBanner = async () => {
      try {
        const res = await api.getPageBanner(pageSlug);
        setBanner(res.data);
      } catch (error) {
        setBanner(null);
      } finally {
        setLoading(false);
      }
    };
    loadBanner();
  }, [pageSlug]);

  if (loading) return null;

  const title = banner?.hero_title || fallbackTitle;
  const subtitle = banner?.hero_subtitle || fallbackSubtitle;
  const hasImage = !!banner?.hero_image;

  // No image, no title? skip rendering
  if (!hasImage && !title) return null;

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ height: `${height}px` }}
      data-testid={`page-hero-${pageSlug}`}
    >
      {hasImage && (
        <img
          src={resolveMediaUrl(banner.hero_image)}
          alt={title || ''}
          className="absolute inset-0 w-full h-full"
          style={{
            objectFit: 'cover',
            objectPosition: `${banner.image_position_x ?? 50}% ${banner.image_position_y ?? 50}%`,
            transform: `scale(${(banner.image_zoom ?? 100) / 100})`,
            transformOrigin: `${banner.image_position_x ?? 50}% ${banner.image_position_y ?? 50}%`,
          }}
        />
      )}
      {/* WPSL diagonal red→blue overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: hasImage
            ? 'linear-gradient(115deg, rgba(203,23,30,0.72) 0%, rgba(203,23,30,0.45) 38%, rgba(0,100,166,0.55) 100%)'
            : 'linear-gradient(115deg, #cb171e 0%, #a01016 45%, #0064a6 100%)',
        }}
      />
      {/* Bottom dark gradient for legibility */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />

      <div className="relative h-full max-w-7xl mx-auto px-4 flex flex-col justify-center">
        {title && (
          <h1
            className="text-white tracking-[0.04em] uppercase"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 200,
              fontSize: 'clamp(40px, 7vw, 96px)',
              lineHeight: 1.05,
            }}
            data-testid={`page-hero-title-${pageSlug}`}
          >
            {title}
          </h1>
        )}
        {subtitle && (
          <p
            className="mt-4 text-white/90 max-w-3xl"
            style={{
              fontFamily: 'Lato, sans-serif',
              fontWeight: 400,
              fontSize: 'clamp(15px, 1.4vw, 19px)',
              lineHeight: 1.5,
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
    </section>
  );
};

export default PageHeroBanner;
